// Wheel of Fortune for late-join bonus
// Canvas-based spinning wheel with predetermined result

import { WHEEL_SEGMENTS } from '@wordsheet/shared';

// Segment colors — alternating vibrant palette
const SEGMENT_COLORS = [
    '#4ecdc4', '#f0c040', '#ff6b6b', '#60a5fa', '#34d399',
    '#c084fc', '#fb923c', '#f472b6', '#4ecdc4', '#f0c040',
    '#ff6b6b', '#60a5fa', '#34d399', '#c084fc', '#fb923c',
    '#f472b6', '#4ecdc4', '#f0c040', '#ff6b6b', '#60a5fa'
];

/**
 * Show the Wheel of Fortune overlay and spin to the predetermined result.
 * @param {number} bonusAmount - The predetermined bonus (must be a value in WHEEL_SEGMENTS)
 * @returns {Promise<void>} Resolves when the wheel dismisses
 */
export function showWheelOfFortune(bonusAmount) {
    return new Promise((resolve) => {
        // Find a segment index matching the bonus amount
        // Pick a random one if multiple match (for visual variety)
        const matchingIndices = WHEEL_SEGMENTS
            .map((v, i) => v === bonusAmount ? i : -1)
            .filter(i => i >= 0);
        const targetIndex = matchingIndices[Math.floor(Math.random() * matchingIndices.length)];

        // Create overlay
        const overlay = document.createElement('div');
        overlay.className = 'wheel-overlay';

        const container = document.createElement('div');
        container.className = 'wheel-container';

        // Title
        const title = document.createElement('div');
        title.className = 'wheel-title';
        title.textContent = 'Late Join Bonus!';
        container.appendChild(title);

        // Canvas wrapper (for pointer positioning)
        const canvasWrapper = document.createElement('div');
        canvasWrapper.className = 'wheel-canvas-wrapper';

        const canvas = document.createElement('canvas');
        canvas.className = 'wheel-canvas';
        canvas.width = 300;
        canvas.height = 300;
        canvasWrapper.appendChild(canvas);

        // Pointer triangle at top
        const pointer = document.createElement('div');
        pointer.className = 'wheel-pointer';
        canvasWrapper.appendChild(pointer);

        container.appendChild(canvasWrapper);

        // Result text (hidden until spin completes)
        const resultEl = document.createElement('div');
        resultEl.className = 'wheel-result';
        resultEl.style.opacity = '0';
        container.appendChild(resultEl);

        overlay.appendChild(container);
        document.body.appendChild(overlay);

        const ctx = canvas.getContext('2d');
        const cx = canvas.width / 2;
        const cy = canvas.height / 2;
        const radius = 140;
        const segCount = WHEEL_SEGMENTS.length;
        const segAngle = (Math.PI * 2) / segCount;

        // Calculate target rotation:
        // The pointer is at top (12 o'clock = -PI/2).
        // We want the target segment centered under the pointer.
        // Segment i spans from i*segAngle to (i+1)*segAngle (measured from 3 o'clock).
        // Center of segment i is at (i + 0.5) * segAngle.
        // For the pointer at top (-PI/2), we need rotation such that:
        //   rotation + segCenter = -PI/2 (mod 2PI)
        //   rotation = -PI/2 - segCenter
        const segCenter = (targetIndex + 0.5) * segAngle;
        const targetRotation = -Math.PI / 2 - segCenter;

        // Add several full rotations for visual effect (4-6 full spins)
        const fullSpins = 4 + Math.floor(Math.random() * 3);
        const totalRotation = targetRotation - fullSpins * Math.PI * 2;

        // Animation
        const duration = 3000; // 3 seconds
        const startTime = performance.now();
        let animId = null;

        function easeOutCubic(t) {
            return 1 - Math.pow(1 - t, 3);
        }

        function drawWheel(rotation) {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Draw segments
            for (let i = 0; i < segCount; i++) {
                const startAngle = rotation + i * segAngle;
                const endAngle = startAngle + segAngle;

                // Segment fill
                ctx.beginPath();
                ctx.moveTo(cx, cy);
                ctx.arc(cx, cy, radius, startAngle, endAngle);
                ctx.closePath();
                ctx.fillStyle = SEGMENT_COLORS[i];
                ctx.fill();

                // Segment border
                ctx.strokeStyle = 'rgba(0,0,0,0.3)';
                ctx.lineWidth = 1;
                ctx.stroke();

                // Label
                const labelAngle = startAngle + segAngle / 2;
                const labelRadius = radius * 0.7;
                const lx = cx + Math.cos(labelAngle) * labelRadius;
                const ly = cy + Math.sin(labelAngle) * labelRadius;

                ctx.save();
                ctx.translate(lx, ly);
                ctx.rotate(labelAngle + Math.PI / 2);
                ctx.fillStyle = '#000';
                ctx.font = 'bold 14px monospace';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(WHEEL_SEGMENTS[i].toString(), 0, 0);
                ctx.restore();
            }

            // Center circle
            ctx.beginPath();
            ctx.arc(cx, cy, 18, 0, Math.PI * 2);
            ctx.fillStyle = '#1a1a2e';
            ctx.fill();
            ctx.strokeStyle = '#f0c040';
            ctx.lineWidth = 2;
            ctx.stroke();

            // Outer ring
            ctx.beginPath();
            ctx.arc(cx, cy, radius + 2, 0, Math.PI * 2);
            ctx.strokeStyle = '#f0c040';
            ctx.lineWidth = 3;
            ctx.stroke();
        }

        function animate(now) {
            const elapsed = now - startTime;
            const t = Math.min(elapsed / duration, 1);
            const eased = easeOutCubic(t);
            const currentRotation = totalRotation * eased;

            drawWheel(currentRotation);

            if (t < 1) {
                animId = requestAnimationFrame(animate);
            } else {
                // Spin complete - show result
                onSpinComplete();
            }
        }

        function playBonusChime() {
            try {
                const actx = new (window.AudioContext || window.webkitAudioContext)();
                const now = actx.currentTime;

                const playNote = (freq, start, dur, gain = 0.12, type = 'square') => {
                    const osc = actx.createOscillator();
                    const g = actx.createGain();
                    osc.connect(g);
                    g.connect(actx.destination);
                    osc.frequency.value = freq;
                    osc.type = type;
                    g.gain.setValueAtTime(gain, start);
                    g.gain.exponentialRampToValueAtTime(0.001, start + dur);
                    osc.start(start);
                    osc.stop(start + dur);
                };

                // Ascending chime: 3 quick notes
                playNote(523.25, now, 0.12);        // C5
                playNote(659.25, now + 0.10, 0.12);  // E5
                playNote(783.99, now + 0.20, 0.20);  // G5
                playNote(1046.50, now + 0.35, 0.30); // C6

                setTimeout(() => actx.close().catch(() => {}), 1500);
            } catch (e) {
                // Audio not available
            }
        }

        function onSpinComplete() {
            playBonusChime();

            resultEl.textContent = `+${bonusAmount} points!`;
            resultEl.style.opacity = '1';

            // Auto-dismiss after 2 seconds or on click
            let dismissed = false;
            function dismiss() {
                if (dismissed) return;
                dismissed = true;
                if (animId) cancelAnimationFrame(animId);
                overlay.remove();
                resolve();
            }

            overlay.addEventListener('click', dismiss);
            overlay.addEventListener('touchend', dismiss);
            setTimeout(dismiss, 2000);
        }

        // Start animation
        animId = requestAnimationFrame(animate);
    });
}
